import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

const either = (a, b) => a === "ja" || b === "ja";

const KRITERIEN_ROWS = [
  { field: "orthopaedischeZusatzkomplikation", label: "a. Orthopädische Zusatzkomplikation" },
  { field: "neurologischeZusatzkomplikation", label: "b. Neurologische Zusatzkomplikation (motorische Funktionseinschränkung / Parese eines oder beider Beine)" },
  { field: "fehlgeschlageneVorversorgung", label: "c. Fehlgeschlagene adäquate Vorversorgung" },
];

function buildKriterienText(kriterien = {}) {
  const selectedLabels = KRITERIEN_ROWS
    .filter((row) => kriterien[row.field] === "ja")
    .map((row) => row.label);
  if (selectedLabels.length === 0) return "";
  return `Sie sollten aufgrund Ihrer Auswahl (${selectedLabels.join(", ")}) Ihre Auswahl erweitern oder Rücksprache mit einer Ärztin / einem Arzt halten.`;
}

function wrapText(text, font, fontSize, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let currentLine = "";
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (font.widthOfTextAtSize(testLine, fontSize) <= maxWidth || !currentLine) {
      currentLine = testLine;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

/**
 * Loads Schuhverordnungsbogen.pdf, fills form fields from patient data, and triggers download.
 * @param {string} pdfUrl - URL to the PDF asset (from CRA import)
 * @param {object} klinischerBefund - data from KlinischerBefundContext
 * @param {object} fussstatus - data from FussstatusContext
 * @param {object} kriterien - data from KriterienContext
 */
export async function generateDdgPdf(pdfUrl, klinischerBefund = {}, fussstatus = {}, kriterien = {}) {
  const existingPdfBytes = await fetch(pdfUrl).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const form = pdfDoc.getForm();

  const checkIf = (fieldName, condition) => {
    if (condition) form.getCheckBox(fieldName).check();
  };

  const kb = klinischerBefund;
  const fs = fussstatus;

  // pAVK
  checkIf("pAVK", either(kb.linksHinweisPAVK, kb.rechtsHinweisPAVK));

  // PNP mit LOPS
  checkIf("PNP mit LOPS", either(kb.linksHinweisPNP, kb.rechtsHinweisPNP));

  // Dialyse
  checkIf("Dialyse", fs.dialysepflichtigeNiereninsuffienz === "ja");

  // Z. n. Zehenamputation
  checkIf("Z n Zehenamputation", either(fs.linksZustandNachZehenamputation, fs.rechtsZustandNachZehenamputation));

  // Präulzerative Veränderungen
  checkIf("Präulzerative Veränderungen", either(fs.linksPraeulzerativeVeranderungen, fs.rechtsPraeulzerativeVeranderungen));

  // Z. n. Ulkus — ulcus ja AND type includes "abgeheilt"
  const linksZnUlkus = fs.linksUlcus === "ja" && fs.linksUlcusTyp?.includes("abgeheilt");
  const rechtsZnUlkus = fs.rechtsUlcus === "ja" && fs.rechtsUlcusTyp?.includes("abgeheilt");
  checkIf("Z n Ulkus", linksZnUlkus || rechtsZnUlkus);

  // Mit Deformitäten / Dysproportionen
  const deformValues = ["ja", "hochgradig"];
  checkIf(
    "Mit Deformitäten  Dysproportionen",
    deformValues.includes(fs.linksFussdeformitaeten) || deformValues.includes(fs.rechtsFussdeformitaeten)
  );

  // CNO inaktiv
  const linksCnoInaktiv = fs.linksHinweisCNO === "ja" && fs.linksHinweisCNOTyp === "inaktiv";
  const rechtsCnoInaktiv = fs.rechtsHinweisCNO === "ja" && fs.rechtsHinweisCNOTyp === "inaktiv";
  checkIf("CNO inaktiv", linksCnoInaktiv || rechtsCnoInaktiv);

  // Z. n. Fußteilamputation
  checkIf("Z n Fußteilamputation", either(fs.linksZustandNachAmputation, fs.rechtsZustandNachAmputation));

  // Fußläsion, aktive CNO — active CNO OR active ulcus (both are acute foot lesions)
  const linksCnoAktiv   = fs.linksHinweisCNO  === "ja" && fs.linksHinweisCNOTyp  === "floride/aktiv";
  const rechtsCnoAktiv  = fs.rechtsHinweisCNO === "ja" && fs.rechtsHinweisCNOTyp === "floride/aktiv";
  const linksUlcusAkut  = fs.linksUlcus  === "ja" && (fs.linksUlcusTyp  || "").includes("akut");
  const rechtsUlcusAkut = fs.rechtsUlcus === "ja" && (fs.rechtsUlcusTyp || "").includes("akut");
  checkIf("Fußläsion aktive CNO", linksCnoAktiv || rechtsCnoAktiv || linksUlcusAkut || rechtsUlcusAkut);

  // Datum fields
  const today = new Date().toLocaleDateString("de-DE");
  const text3 = form.getTextField("Text3");
  text3.setText(today);
  text3.setFontSize(9);
  const text4 = form.getTextField("Text4");
  text4.setText(today);
  text4.setFontSize(9);

  // "Sonstige relevante Diagnosen" — dynamic text from Kriterien, with word-wrap
  const sonstigeText = buildKriterienText(kriterien);
  if (sonstigeText) {
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const page = pdfDoc.getPages()[0];
    const fontSize = 9;
    const lineHeight = 11;
    const startX = 323;
    const startY = 98;
    const maxWidth = 480;

    const lines = wrapText(sonstigeText, font, fontSize, maxWidth);
    lines.forEach((line, i) => {
      page.drawText(line, {
        x: startX,
        y: startY - i * lineHeight,
        size: fontSize,
        font,
        color: rgb(0, 0, 0),
      });
    });
  }

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "Schuhverordnungsbogen.pdf";
  link.click();
  URL.revokeObjectURL(url);
}
